<script setup>

import { computed, inject, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { DateTime } from 'luxon'
import { formatDate } from "../lib/es-bill-shared-js/misc/timeTools";
import { useDsoInformationStore } from "@/stores/dsoCompanyInformationStore";
import { useCommunityStore } from "@/stores/communityStore";
import { resolveComboboxItem, isComboboxItem } from "@/utils/comboboxUtils";

// ANFORDERUNG_ECP for the selected community, shown in the podlist viewer
const props = defineProps({
  // community entry of the viewer select: { community, ecn, ecid, overwriteEcnDso }
  communityEntry: { type: Object, required: true },
});

const $log = inject('log');
const { t } = useI18n();

const communityStore = useCommunityStore();
const dsoInformationStore = useDsoInformationStore();
// async loading in background
dsoInformationStore.fetchDsoInformation();

const dsoAvailable = computed(() => {
  return dsoInformationStore.informations.map(dsoInfo => ({
    ...dsoInfo,
    title: dsoInfo.name,
    props: { subtitle: dsoInfo.ecn },
    ecn: dsoInfo.ecn,
  }));
});

const selection = ref({
  dsoEntry: null,
  defaultTimeRange: true,
  dateRange: [],
});

watch(() => selection.value.dsoEntry, value => {
  selection.value.dsoEntry = resolveComboboxItem(value, dsoAvailable.value, ['ecn', 'title']);
});

const requestRunning = ref(false);
const requestFailed = ref(false);
const requestSent = ref(false);

watch(() => props.communityEntry, () => {
  requestFailed.value = false;
  requestSent.value = false;
});

const dateStartDefault = DateTime.local(2020, 1, 1).toJSDate();
const dateEndDefault = DateTime.local().minus({days: 1}).
  set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toJSDate();

const requestParameters = computed(() => {
  let dr = [];
  if(selection.value.defaultTimeRange) {
    dr = [dateStartDefault, dateEndDefault]
  } else if(selection.value.dateRange.length === 1) {
    const dateStart = DateTime.fromISO(selection.value.dateRange[0]);
    dr = [dateStart.toJSDate(), dateStart.toJSDate()];
  }
  else if(selection.value.dateRange.length > 1) {
    const dateStart = DateTime.fromISO(selection.value.dateRange[0]);
    let dateEnd = DateTime.fromISO(selection.value.dateRange[
      selection.value.dateRange.length-1]);

    dateEnd=dateEnd.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    dr = [dateStart.toJSDate(), dateEnd.toJSDate()]
  }

  let dsoToUse = isComboboxItem(selection.value.dsoEntry) ? selection.value.dsoEntry : null;
  if(props.communityEntry.overwriteEcnDso)
    dsoToUse = dsoInformationStore.informations.find(
      dso => dso.ecn === props.communityEntry.overwriteEcnDso)

  return {
    community: props.communityEntry.community || null,
    dso: dsoToUse,
    dateRange: dr,
    canBeRequested: dr.length && props.communityEntry.community && dsoToUse,
  }
})

async function startRequest() {
  if(!requestParameters.value.canBeRequested)
    return;

  requestRunning.value = true;
  requestFailed.value = false;
  requestSent.value = false;

  try {
    await communityStore.startEcPodlist(
      requestParameters.value.community.ecn,
      requestParameters.value.community.ecid,
      requestParameters.value.dateRange[0],
      requestParameters.value.dateRange[1],
      requestParameters.value.dso.ecn,
    )
    requestSent.value = true;
  } catch (e) {
    $log.error(e)
    requestFailed.value = true;
  }

  requestRunning.value = false;
}

</script>
<script>
export default {
  name: 'PodlistRequestPanel',
}
</script>


<template>
  <div>
    <p class="textHeadline">{{ $t('ec-podlist-request') }}</p>
    ANFODERUNG_ECP für 16a, 16b, 16c<br/>
    DSO für 16a, 16c aus community(Technical).ecnDso, Dropdown für 16b<br/>
    <br/>

    <v-combobox
      :disabled="!!communityEntry.overwriteEcnDso?.length"
      max-width="400px"
      bg-color="white"
      :class="{'mr-0': $vuetify.display.xs}"
      :placeholder="t('dso')"
      v-model="selection.dsoEntry"
      :items="dsoAvailable"
      :filter-keys="['title', 'value']"
      density="comfortable"
      variant="outlined"
      item-value="ecn"
      item-title="name"
      rounded="2"
      hide-details
      clearable
    ></v-combobox>

    <v-checkbox density="compact"
                v-model="selection.defaultTimeRange"
                :label="t('defaultTimeRange')"
                hint="use 1.1.2020 to yesterday"
                persistent-hint
                max-width="200px"
                class="mb-4 align-checkbox-start"
    />

    <v-date-picker v-if="!selection.defaultTimeRange"
                   v-model="selection.dateRange"
                   show-adjacent-months
                   hide-header
                   width="350" multiple='range'>
    </v-date-picker>

    Gemeinschaft: {{ requestParameters.community?.name}}<br/>
    ECID: {{ requestParameters.community?.ecid}}<br/>
    ECN: {{ requestParameters.community?.ecn}}<br/>
    VNB: {{ requestParameters.dso?.name}}<br/>
    ECN-DSO: {{ requestParameters.dso?.ecn}}<br/>
    DateRange: {{ formatDate(requestParameters.dateRange[0]) }} bis inkl.
    {{ formatDate(requestParameters.dateRange[1]) }}<br/>

    <br/>
    <PrimaryButton
      :disabled="!requestParameters.canBeRequested || requestRunning"
      @click="startRequest"
      :text="$t('startRequest')"
    />

    <v-row v-if="requestRunning">
      <v-progress-circular class="mx-auto" color="blue-lighten-3" indeterminate></v-progress-circular>
    </v-row>
    <div v-if="requestSent" class="mt-2">
      Anfrage gesendet, die Podlist kommt als neue Nachricht unten in die Liste
    </div>
    <div v-if="requestFailed" class="mt-2">
      Fehler bei Anfrage
    </div>
  </div>
</template>
